import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    Signal,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { IQuizNotification } from '../../../../fi-quiz.d';
import { cloneDeep } from '@fi-app/shared/utils/clone-deep';
import { FiValuesComparison } from '@fi-app/shared/utils/fi-values-comparison';
import { FiDialogsService } from '@fi-app/shared/fi-dialogs/fi-dialogs.service';
import { FiQuizNotificationRecipientType } from '../../../../fi-quiz-enums';
import { FiValidatingFormDirective } from '@fi-app/shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { AutomatedActionRecipientActionType } from '@fi-app/shared/automated-action-recipient-select/automated-action-recipient-select-enum';
import { AppTextService } from '@fi-app/shared/app-text';
import { DeepReadonly } from '@fi-app/shared/types/deep-readonly';
import { INameIdObj } from '@fi-app/shared/fi-shared-interfaces';
import { FiQuizEditorStoreService } from '../../../fi-quiz-editor-store.service';
import { Animations } from '@fi-app/shared/utils/animations';
import { AccessRightFilterCollectionModel } from '@fi-app/shared/models/access-right-filter-collection.model';

@Component({
    selector: 'fi-quiz-notification-dialog',
    templateUrl: './quiz-notification-dialog.component.html',
    styleUrls: ['./quiz-notification-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-quiz-notification-dialog' },
    standalone: false,
    animations: [Animations.fadeHeight()],
})
export class FiQuizNotificationDialogComponent extends FiDialogsContentBase<IQuizNotification, IQuizNotification> {
    private readonly fiDialogsService = inject(FiDialogsService);
    private readonly appTextService = inject(AppTextService);
    private readonly fiQuizEditorStoreService = inject(FiQuizEditorStoreService);

    protected sendNotificationEditorForm = viewChild<FiValidatingFormDirective>('sendNotificationEditorForm');

    protected notification: IQuizNotification = cloneDeep(this.data);
    protected fiValuesComparison = new FiValuesComparison(() => this.notification);
    protected accessRightFilterCollection: Signal<AccessRightFilterCollectionModel> =
        this.fiQuizEditorStoreService.accessRightFilterCollection;

    public getResult(): IQuizNotification {
        if (this.isNotificationMethodHidden(this.notification.RecipientType)) {
            this.notification.ViaNotification = false;
            this.notification.ViaSMS = false;
            this.notification.ViaEmail = false;
        }
        return this.notification;
    }

    public getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        return this.fiDialogsService.getCanCloseConfirmation(!this.fiValuesComparison.isChanged());
    }

    public isYesButtonDisabled(): boolean {
        return !!this.sendNotificationEditorForm()?.errors || this.sendNotificationEditorForm()?.pending;
    }

    protected isNotificationMethodHidden(recipientType: FiQuizNotificationRecipientType): boolean {
        return this.directNotificationMethod.includes(recipientType);
    }

    private readonly directNotificationMethod = [
        FiQuizNotificationRecipientType.EmailAddress,
        FiQuizNotificationRecipientType.SMS,
    ];

    protected readonly RecipientTypeOptions: DeepReadonly<INameIdObj[]> = [
        {
            Id: FiQuizNotificationRecipientType.RolesLocal,
            Name: this.appTextService.get('checklist.checkpoint_action_recipient_local_roles'),
        },
        {
            Id: FiQuizNotificationRecipientType.Roles,
            Name: this.appTextService.get('checklist.checkpoint_action_recipient_roles'),
        },
        {
            Id: FiQuizNotificationRecipientType.Users,
            Name: this.appTextService.get('checklist.checkpoint_action_recipient_staff_members'),
        },
        {
            Id: FiQuizNotificationRecipientType.EmailAddress,
            Name: this.appTextService.get('checklist.checkpoint_action_recipient_email_address'),
        },
        {
            Id: FiQuizNotificationRecipientType.SMS,
            Name: this.appTextService.get('checklist.checkpoint_action_recipient_sms'),
        },
    ];
    protected readonly AutomatedActionRecipientActionType = AutomatedActionRecipientActionType;
}
